import type {
  CustomerNameMatch,
  CustomerSearchResult
} from "../../../CustomersPage/types/customer.types.ts"

type CustomerMatchReason = "email" | "phone" | "name"

interface CustomerMatchCandidate {
  id: string | null
  first_name: string | null
  middle_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  address: string | null
  address_2: string | null
  city: string | null
  state: string | null
  zip_code: string | null
  is_incomplete: boolean | null
  project_count: number | null
  is_suspended: boolean
  reason: CustomerMatchReason
}

const REASON_RANK: Record<CustomerMatchReason, number> = {
  email: 0,
  phone: 1,
  name: 2
}

function normalizeEmailForMatch(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase()
}

function phoneDigits(value: string | null | undefined): string {
  return (value ?? "").replace(/\D/g, "")
}

const MIN_MATCHABLE_PHONE_DIGITS = 7
const US_NATIONAL_DIGITS = 10

// A common first name matches every customer that shares it, and the panel is
// warn-only: an unbounded list pushes the form it belongs to off the page.
const MAX_VISIBLE_MATCHES = 5
// One past what is shown, so a full page is itself the signal that more exist —
// no second count query, and the endpoint is never asked for the whole set.
const NAME_MATCH_PAGE_SIZE = MAX_VISIBLE_MATCHES + 1

// The stored column is not canonical, so a leading US country code is a
// formatting variant here. The server's identity rule keeps it significant —
// which is precisely why the two spellings become duplicates it will not merge,
// and why the warning has to see them as one customer.
function comparablePhoneDigits(value: string | null | undefined): string {
  const digits = phoneDigits(value)

  return digits.startsWith("1") && digits.length > US_NATIONAL_DIGITS
    ? digits.slice(1)
    : digits
}

function isEmailMatch(
  left: string | null | undefined,
  right: string | null | undefined
): boolean {
  const a = normalizeEmailForMatch(left)
  return !!a && a === normalizeEmailForMatch(right)
}

function isPhoneMatch(
  left: string | null | undefined,
  right: string | null | undefined
): boolean {
  const a = comparablePhoneDigits(left)
  return (
    a.length >= MIN_MATCHABLE_PHONE_DIGITS && a === comparablePhoneDigits(right)
  )
}

function displayNameOf(
  candidate: Pick<
    CustomerMatchCandidate,
    "first_name" | "middle_name" | "last_name"
  >
): string {
  return [candidate.first_name, candidate.middle_name, candidate.last_name]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ")
}

// An autocomplete row carries no id, so identity has to come from its handles;
// two id-less rows sharing all three are indistinguishable by design.
function candidateKey(candidate: CustomerMatchCandidate): string {
  if (candidate.id) return `id:${candidate.id}`

  return [
    normalizeEmailForMatch(candidate.email),
    comparablePhoneDigits(candidate.phone),
    displayNameOf(candidate).toLowerCase()
  ].join("|")
}

interface TypedCustomerHandles {
  email?: string | null
  phone?: string | null
}

function reasonFor(
  candidate: Pick<CustomerMatchCandidate, "email" | "phone">,
  typed: TypedCustomerHandles,
  fallback: CustomerMatchReason
): CustomerMatchReason {
  if (isEmailMatch(typed.email, candidate.email)) return "email"
  if (isPhoneMatch(typed.phone, candidate.phone)) return "phone"
  return fallback
}

function fromNameMatch(
  match: CustomerNameMatch,
  typed: TypedCustomerHandles
): CustomerMatchCandidate {
  return {
    id: match.id,
    first_name: match.first_name,
    middle_name: match.middle_name,
    last_name: match.last_name,
    email: match.email,
    phone: match.phone,
    address: null,
    address_2: null,
    city: null,
    state: null,
    zip_code: null,
    is_incomplete: match.is_incomplete ?? null,
    project_count: match.project_count ?? null,
    is_suspended: !!match.is_suspended,
    reason: reasonFor(match, typed, "name")
  }
}

function fromSearchResult(
  result: CustomerSearchResult,
  typed: TypedCustomerHandles,
  fallback: CustomerMatchReason = "name"
): CustomerMatchCandidate {
  return {
    id: result.id ?? null,
    first_name: result.first_name,
    middle_name: result.middle_name,
    last_name: result.last_name,
    email: result.email,
    phone: result.phone,
    address: result.address,
    address_2: result.address_2,
    city: result.city,
    state: result.state,
    zip_code: result.zip_code,
    is_incomplete: null,
    project_count: null,
    is_suspended: !!result.is_suspended,
    reason: reasonFor(result, typed, fallback)
  }
}

/**
 * Ranked email > phone > name, strongest reading of a duplicate first. Two
 * readings of the same customer collapse into one row keeping the stronger
 * reason, and an id-bearing copy wins over an id-less one so the row the user
 * acts on is the one that can actually be linked.
 */
function rankCustomerMatches(
  candidates: CustomerMatchCandidate[]
): CustomerMatchCandidate[] {
  const merged = new Map<string, CustomerMatchCandidate>()

  for (const candidate of candidates) {
    const key = candidateKey(candidate)
    const existing = merged.get(key)

    if (!existing) {
      merged.set(key, candidate)
      continue
    }

    merged.set(key, {
      ...existing,
      ...candidate,
      id: existing.id ?? candidate.id,
      // Only the name search knows about suspension, so the reading that saw it
      // must survive being merged with one that could not.
      is_suspended: existing.is_suspended || candidate.is_suspended,
      reason:
        REASON_RANK[candidate.reason] < REASON_RANK[existing.reason]
          ? candidate.reason
          : existing.reason
    })
  }

  return [...merged.values()].sort(
    (a, b) => REASON_RANK[a.reason] - REASON_RANK[b.reason]
  )
}

export {
  type CustomerMatchCandidate,
  type CustomerMatchReason,
  type TypedCustomerHandles,
  MAX_VISIBLE_MATCHES,
  MIN_MATCHABLE_PHONE_DIGITS,
  NAME_MATCH_PAGE_SIZE,
  candidateKey,
  comparablePhoneDigits,
  displayNameOf,
  fromNameMatch,
  fromSearchResult,
  isEmailMatch,
  isPhoneMatch,
  normalizeEmailForMatch,
  phoneDigits,
  rankCustomerMatches
}
